import { useEffect, useState } from "react";
import type { ArticleSection } from "@/types/article";

interface TableOfContentsProps {
  sections: ArticleSection[];
}

export default function TableOfContents({ sections }: TableOfContentsProps) {
  const [active, setActive] = useState(sections[0]?.id || "");
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => {
      let current = sections[0]?.id || "";
      sections.forEach((section) => {
        const el = document.getElementById(section.id);
        if (el && el.getBoundingClientRect().top <= 140) current = section.id;
      });
      setActive(current);
    };
    onScroll();
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, [sections]);

  const scrollTo = (id: string) => {
    const el = document.getElementById(id);
    if (el) {
      const top = el.getBoundingClientRect().top + window.scrollY - 100;
      window.scrollTo({ top, behavior: "smooth" });
    }
    setOpen(false);
  };

  return (
    <>
      <aside className="hidden lg:block">
        <div className="sticky top-28 rounded-lg border border-foreground-200/70 bg-background-100 p-5">
          <h3 className="mb-4 flex items-center gap-2 font-heading text-base font-bold text-foreground-950">
            <i className="ri-list-check text-primary-500" /> 本文目录
          </h3>
          <nav className="flex flex-col gap-1">
            {sections.map((section, i) => (
              <button
                key={section.id}
                type="button"
                onClick={() => scrollTo(section.id)}
                className={`flex items-start gap-2 rounded-md px-3 py-2 text-left text-sm transition-colors cursor-pointer ${
                  active === section.id
                    ? "bg-background-50 font-medium text-primary-600"
                    : "text-foreground-600 hover:bg-background-50 hover:text-foreground-950"
                }`}
              >
                <span className="text-xs text-foreground-400">0{i + 1}</span>
                <span className="line-clamp-2">{section.heading}</span>
              </button>
            ))}
          </nav>
        </div>
      </aside>

      <div className="lg:hidden">
        <button
          type="button"
          onClick={() => setOpen((v) => !v)}
          className="fixed bottom-24 right-5 z-40 flex h-12 w-12 items-center justify-center rounded-full bg-primary-500 text-xl text-background-50"
          aria-label="打开目录"
        >
          <i className={open ? "ri-close-line" : "ri-list-check"} />
        </button>
        {open && (
          <div className="fixed inset-0 z-[55]">
            <div className="absolute inset-0 bg-foreground-950/40" onClick={() => setOpen(false)} aria-hidden="true" />
            <div className="absolute inset-x-0 bottom-0 max-h-[70vh] overflow-y-auto rounded-t-2xl bg-background-50 p-5">
              <h3 className="mb-4 font-heading text-lg font-bold text-foreground-950">本文目录</h3>
              <nav className="flex flex-col gap-1">
                {sections.map((section, i) => (
                  <button
                    key={section.id}
                    type="button"
                    onClick={() => scrollTo(section.id)}
                    className={`flex items-start gap-2 rounded-md px-3 py-3 text-left text-sm transition-colors ${
                      active === section.id ? "bg-background-100 font-medium text-primary-600" : "text-foreground-700"
                    }`}
                  >
                    <span className="text-xs text-foreground-400">0{i + 1}</span>
                    {section.heading}
                  </button>
                ))}
              </nav>
            </div>
          </div>
        )}
      </div>
    </>
  );
}